"use client";

import React, { useState, useRef, useEffect } from "react";
import Link from "next/link";
import { Clock, MapPin } from "lucide-react";

const StaticGoogleMap: React.FC = () => {
  const [loadMap, setLoadMap] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);
  
  const dynamicMapUrl = "https://maps.google.com/maps?cid=8192593597492375843";
  const embedMapUrl = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3022.1040487013897!2d-73.91802349999999!3d40.7597362!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c25fc39318e9fb%3A0x71b1f0787fac3123!2ssmile4me%20Dental%20Care%20-%20Astoria%2C%20Queens!5e0!3m2!1sen!2sus!4v1755813645127!5m2!1sen!2sus";

  const officeHours = [
    { day: "Monday", time: "10:00 am - 7:00 pm" },
    { day: "Tuesday", time: "10:00 am - 7:00 pm" },
    { day: "Wednesday", time: "10:00 am - 7:00 pm" },
    { day: "Thursday", time: "10:00 am - 7:00 pm" },
    { day: "Friday", time: "10:00 am - 2:00 pm" },
    { day: "Saturday", time: "Closed" },
    { day: "Sunday", time: "11:00 am - 5:00 pm" }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setLoadMap(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    if (mapRef.current) {
      observer.observe(mapRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleMapClick = () => {
    setLoadMap(true);
  };

  const getCurrentDay = () => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[new Date().getDay()];
  };

  const currentDay = getCurrentDay();

  return (
    <section
      className="relative w-full bg-gray-50 py-16"
      role="region"
      aria-labelledby="map-heading"
    >
      <div className="max-w-6xl mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2
            id="map-heading"
            className="text-3xl md:text-4xl font-bold text-gray-900 mb-4"
          >
            Visit Us at Smile4Me
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-[#AE7D2F] to-[#ECD05B] mx-auto"></div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          
          {/* Left Side - Map */}
          <div>
            <div 
              ref={mapRef}
              className="w-full h-[300px] lg:h-[400px] rounded-lg shadow-lg overflow-hidden relative"
            >
              {!loadMap ? (
                <div 
                  className="w-full h-full bg-gray-100 flex flex-col items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors group p-4"
                  onClick={handleMapClick}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleMapClick();
                    }
                  }}
                  aria-label="Load interactive map of Smile4Me location"
                >
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-4 bg-[#AE7D2F] rounded-full flex items-center justify-center group-hover:bg-[#ECD05B] transition-colors">
                      <MapPin className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Click to Load Map</h3>
                    <p className="text-sm text-gray-500">Interactive map of our location</p>
                    <p className="text-xs text-gray-400 mt-2">Smile4Me Dental Care<br />Astoria, Queens</p>
                  </div>
                </div>
              ) : (
                <iframe
                  src={embedMapUrl}
                  className="w-full h-full border-0"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Interactive map of Smile4Me location"
                />
              )}
            </div>
            
            {/* Map Link */}
            <div className="mt-4 text-center">
              <Link
                href={dynamicMapUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-[#AE7D2F] hover:text-[#ECD05B] font-medium transition-colors p-2 rounded"
                aria-label="Open Google Maps in new tab"
              >
                <MapPin className="w-4 h-4 mr-2" />
                Open in Google Maps
              </Link>
            </div>
          </div>

          {/* Right Side - Office Hours */}
          <div>
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-center mb-6">
                <Clock className="w-6 h-6 text-[#AE7D2F] mr-3" />
                <h3 className="text-2xl font-bold text-gray-900">Office Hours</h3>
              </div>
              
              <div className="space-y-3">
                {officeHours.map((schedule, index) => (
                  <div 
                    key={index} 
                    className={`flex justify-between items-center py-2 px-3 rounded ${
                      schedule.day === currentDay 
                        ? 'bg-gradient-to-r from-[#AE7D2F] to-[#ECD05B] text-white font-semibold' 
                        : 'text-gray-700'
                    }`}
                  >
                    <span className="font-medium">{schedule.day}</span>
                    <span className={schedule.time === 'Closed' ? 'text-red-500 font-medium' : ''}>{schedule.time}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default StaticGoogleMap;